import { useSyncExternalStore } from 'react'
import { useIsMutating, type QueryClient } from '@tanstack/react-query'

/**
 * The one post-mutation refresh in the app. `App.tsx` wires it into the
 * QueryClient's `MutationCache`, so every mutation — present and future — gets
 * it for free and no call site hand-maintains invalidation keys.
 *
 * Two deliberate design choices, both of which look like bugs if you don't know
 * why they're here:
 *
 * 1. **A bare `invalidateQueries()` with no key.** Every active query refetches.
 *    An allowlist of keys was considered and rejected: it's the same
 *    hand-maintained bookkeeping that produced the bug this replaces (34 write
 *    sites each owning their own keys, several invalidating nothing at all), and
 *    the next mutation to forget its entry would be silently stale again. There
 *    are ~12 query keys in this app and the payloads are small. A blanket
 *    invalidation cannot be forgotten. Don't "optimize" this back into a list.
 *
 * 2. **It repeats on a schedule instead of firing once.** Most gateway write
 *    endpoints don't commit before they respond: they `handle.signal(...)` a
 *    Temporal workflow and return `{"ok": true}` immediately, and the Postgres
 *    row is written later by a worker activity. `/tasks/{id}/review` (reject),
 *    `/tasks/{id}/maintainer-decision` and
 *    `/studies/{uid}/seg-failure-decision` measured 100–800 ms of gap. A single
 *    immediate refetch on those paths is *guaranteed* to read the old row — it
 *    is correct code producing a stale screen. The trailing steps absorb that
 *    window. This is not a mystery poll; deleting it brings the bug back for
 *    every signal-then-return endpoint.
 */
// The maintainer's Pick / Reject-and-Quarantine on the anomaly tiebreak
// signals `anomaly-{uid}` and returns before the workflow's finalize
// activity (index_anomaly + patch_study_status + mark_task_done) has
// committed. On slow days that pipeline lags 8–15s, which is longer than
// the original 5s tail — the /work?tab=decide counter refreshes but the
// server still says the study is flagged, so the count sticks at 1 for a
// moment after the maintainer's click. Extending the tail to 15s (and
// adding a 30s safety net) means the counter reflects the real
// server-side state without the user needing to reload.
const SCHEDULE_MS = [0, 700, 2_000, 5_000, 10_000, 15_000, 30_000] as const

// One timer set for the whole module, not one per call. A burst still in flight
// is cancelled and restarted by the next mutation, so ten rapid decisions
// schedule one burst rather than ten overlapping ones hammering the gateway.
let pending: ReturnType<typeof setTimeout>[] = []

export function refreshAfterMutation(queryClient: QueryClient): void {
  cancelPendingRefresh()
  markSettling()
  pending = SCHEDULE_MS.map((delay) =>
    setTimeout(() => {
      void queryClient.invalidateQueries()
    }, delay),
  )
}

/** Clears anything still scheduled. Also keeps tests from leaking timers. */
export function cancelPendingRefresh(): void {
  pending.forEach(clearTimeout)
  pending = []
  if (settleTimer) {
    clearTimeout(settleTimer)
    settleTimer = null
  }
  settlingUntil = 0
  emitSettling()
}

// ---------------------------------------------------------------------------
// "Still settling" — what the global activity bar reads
// ---------------------------------------------------------------------------

/**
 * How long the UI keeps saying "working" after a mutation resolves.
 *
 * Deliberately **much shorter than `SCHEDULE_MS`**. The burst runs out to 30 s
 * because the anomaly finalize pipeline can lag that far, but an indicator held
 * for 30 s after every click is permanently on, which is worse than no
 * indicator at all — it stops meaning anything. 2.5 s covers the 0 / 700 / 2000
 * steps, which is where the screen actually repaints for the common case; the
 * later steps stay a silent safety net.
 *
 * So this tracks *the user is waiting*, not *the refresh schedule is finished*.
 * Those are different questions and only the first one belongs on screen.
 */
const SETTLE_TAIL_MS = 2_500

let settleTimer: ReturnType<typeof setTimeout> | null = null
let settlingUntil = 0
const settleListeners = new Set<() => void>()

function emitSettling(): void {
  settleListeners.forEach((l) => l())
}

function markSettling(): void {
  settlingUntil = Date.now() + SETTLE_TAIL_MS
  if (settleTimer) clearTimeout(settleTimer)
  settleTimer = setTimeout(() => {
    settleTimer = null
    settlingUntil = 0
    emitSettling()
  }, SETTLE_TAIL_MS)
  emitSettling()
}

function subscribeSettling(onChange: () => void): () => void {
  settleListeners.add(onChange)
  return () => settleListeners.delete(onChange)
}

function isSettling(): boolean {
  return settlingUntil > Date.now()
}

/**
 * True while a click is still working its way through: a mutation in flight, or
 * the short window afterwards where the refresh burst is repainting.
 *
 * Read by `GlobalActivityBar`. Deliberately **not** `useIsFetching()`: the
 * dashboard's case panel polls on an interval, and a fetch-driven indicator
 * would blink continuously on an idle screen until it read as decoration.
 * `useIsMutating()` covers the in-flight half exactly and starts the moment the
 * button is pressed; the tail above covers the gap between "the endpoint
 * answered" and "the list shows it", which is the part users read as stuck.
 */
export function useSettling(): boolean {
  const mutating = useIsMutating() > 0
  const tail = useSyncExternalStore(subscribeSettling, isSettling, () => false)
  return mutating || tail
}
